import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, Ruler, ShoppingBag, LogOut, User, Coins, Bell, Eye, Globe, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showcase, setShowcase] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/credits/balance").then((r) => setCredits(r.data.credits_remaining)).catch(() => {});
    api.get("/showcase/settings").then((r) => setShowcase(r.data)).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    const load = () =>
      api.get("/notifications").then((r) => { setNotifs(r.data.notifications || []); setUnread(r.data.unread || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const openNotifs = (open) => {
    if (open && unread > 0) {
      api.post("/notifications/read").then(() => setUnread(0)).catch(() => {});
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return "adesso";
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h fa`;
    return `${Math.floor(h / 24)} g fa`;
  };

  const links = [
    { to: "/studio", label: "Studio Stylista", icon: Sparkles, testid: "nav-link-stylist" },
    { to: "/wardrobe", label: "Guardaroba", icon: ShoppingBag, testid: "nav-link-wardrobe" },
    { to: "/credits", label: "Crediti", icon: Coins, testid: "nav-link-credits" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#dce6ed]/80 border-b border-[#1e5fe0]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/studio" : "/"} data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-[#1e5fe0]/10 border border-[#1e5fe0]/30 group-hover:bg-[#1e5fe0]/20 transition-colors">
            <Ruler className="h-5 w-5 text-[#2f74f0]" />
          </span>
          <span className="font-serif text-xl font-bold tracking-tight text-[#2e2015]">
My <span className="text-[#2f74f0]">Look</span>
          </span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => {
              const active = location.pathname.startsWith(l.to);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#1e5fe0]/15 text-[#1e5fe0]"
                      : "text-[#6f5c47] hover:bg-[#1e5fe0]/10 hover:text-[#1a4fc0]"
                  }`}
                >
                  <l.icon className="h-4 w-4" />
                  {l.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/credits"
                data-testid="nav-credits-badge"
                className="hidden sm:flex items-center gap-1.5 text-sm text-[#1e5fe0] border border-[#1e5fe0]/30 rounded-full px-3 py-1 hover:bg-[#1e5fe0]/10 transition-colors"
              >
                <Coins className="h-3.5 w-3.5" />
                {credits ?? "…"} crediti
              </Link>

              <DropdownMenu onOpenChange={openNotifs}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-notifications-btn"
                    className="relative grid place-items-center h-9 w-9 rounded-full text-[#6f5c47] hover:bg-[#1e5fe0]/10 hover:text-[#1a4fc0] transition-colors"
                    aria-label="Notifiche"
                  >
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                      <span data-testid="nav-notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1e5fe0] text-white text-[10px] font-bold grid place-items-center">
                        {unread > 9 ? "9+" : unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                  <DropdownMenuLabel className="font-serif">Notifiche</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#1e5fe0]/10" />
                  {notifs.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-[#8a7760]">Nessuna notifica</div>
                  ) : (
                    <div className="max-h-80 overflow-auto">
                      {notifs.map((n) => (
                        <button
                          key={n.id}
                          data-testid="notification-item"
                          onClick={() => navigate(n.handle ? `/vetrina/${n.handle}` : "/wardrobe")}
                          className={`w-full text-left flex items-start gap-3 px-3 py-2.5 hover:bg-[#1e5fe0]/5 ${n.read ? "" : "bg-[#1e5fe0]/5"}`}
                        >
                          <span className="grid place-items-center h-8 w-8 rounded-full bg-[#a9772f]/10 border border-[#a9772f]/30 shrink-0">
                            <Eye className="h-4 w-4 text-[#a9772f]" />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm text-[#3a2a1c] truncate">{n.message}</span>
                            <span className="block text-xs text-[#8a7760]">{timeAgo(n.created_at)}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu-btn"
                    className="hidden md:flex items-center gap-2 text-sm text-[#6f5c47] hover:text-[#1a4fc0] px-2 py-1 rounded-md hover:bg-[#1e5fe0]/10 transition-colors"
                  >
                    <User className="h-4 w-4 text-[#2f74f0]" />
                    {user.name}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                  <DropdownMenuLabel className="font-serif">Il mio profilo</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#1e5fe0]/10" />
                  {showcase?.enabled && showcase?.handle && (
                    <DropdownMenuItem
                      data-testid="nav-showcase-open"
                      onClick={() => window.open(`/vetrina/${showcase.handle}`, "_blank")}
                      className="cursor-pointer focus:bg-[#1e5fe0]/10"
                    >
                      <ExternalLink className="mr-2 h-4 w-4 text-[#2f74f0]" /> Apri la mia vetrina
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-testid="nav-showcase-manage"
                    onClick={() => navigate("/wardrobe?vetrina=1")}
                    className="cursor-pointer focus:bg-[#1e5fe0]/10"
                  >
                    <Globe className="mr-2 h-4 w-4 text-[#a9772f]" /> {showcase?.enabled ? "Gestisci vetrina" : "Crea la tua vetrina"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#1e5fe0]/10" />
                  <DropdownMenuItem
                    data-testid="nav-logout-menu-item"
                    onClick={() => { logout(); navigate("/"); }}
                    className="cursor-pointer focus:bg-red-500/10 text-[#6f5c47]"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                data-testid="nav-logout-btn"
                variant="ghost"
                size="sm"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="md:hidden text-[#6f5c47] hover:text-[#1e5fe0] hover:bg-[#1e5fe0]/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              data-testid="nav-auth-button"
              onClick={() => navigate("/auth")}
              className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-5"
            >
              Accedi
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
